import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { CrmWorkspacesStore } from '@pinnakl/crm/data-access';
import { WorkspaceCode } from '@pinnakl/crm/domain';
import { PinnaklUIToastMessage } from '@pinnakl/shared/util-providers';
import { switchMap } from 'rxjs/operators';
import { CommunicationStore } from '../../../features/communication/communication.store';
import { AuthenticationService } from '../../auth/authentication.service';
import { OutlookWorkspaceService } from '../../services/outlook-workspace.service';
import { UserService } from '../../services/user.service';
import { OutlookUserProfileComponent } from '../user-profile/outlook-user-profile.component';

const WORKSPACE_ICONS: Partial<Record<WorkspaceCode, string>> = {
  LP: 'pi-dollar',
  GP: 'pi-building',
  INVESTMENTS: 'pi-chart-line',
};

@Component({
  selector: 'lib-header-user-profile',
  templateUrl: './header-user-profile.component.html',
  imports: [OutlookUserProfileComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderUserProfileComponent {
  private readonly crmWorkspacesStore = inject(CrmWorkspacesStore);
  private readonly authService = inject(AuthenticationService);
  private readonly userService = inject(UserService);
  private readonly communicationStore = inject(CommunicationStore);
  private readonly router = inject(Router);
  private readonly toast = inject(PinnaklUIToastMessage);
  private readonly destroyRef = inject(DestroyRef);
  private readonly outlookWorkspace = inject(OutlookWorkspaceService);

  protected readonly workspaces = this.crmWorkspacesStore.workspaces;
  protected readonly selectedWorkspaceId = this.crmWorkspacesStore.selectedWorkspaceId;
  protected readonly workspaceChanging = signal(true);

  constructor() {
    this.prepareAutoSaveWorkspace();
  }

  protected workspaceIcon(code: WorkspaceCode): string {
    return WORKSPACE_ICONS[code] ?? 'pi-briefcase';
  }

  protected async selectWorkspace(idToSelect: number): Promise<void> {
    if (idToSelect === this.selectedWorkspaceId() || this.workspaceChanging()) {
      return;
    }
    this.workspaceChanging.set(true);
    try {
      await this.outlookWorkspace.persistSelection(idToSelect);
      this.crmWorkspacesStore.selectWorkspace(idToSelect);
      window.location.reload();
    } catch {
      this.toast.error('Workspace unchanged: unable to prepare CRM auto-save safely. Please try again.');
    } finally {
      this.workspaceChanging.set(false);
    }
  }

  protected logout(): void {
    this.authService
      .logout()
      .pipe(
        switchMap(() => this.userService.clearUser()),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.communicationStore.reset();
          this.crmWorkspacesStore.reset();
          this.router.navigate(['/login'], { skipLocationChange: true });
        },
        error: () => this.toast.error('Unable to log out.'),
      });
  }

  private async prepareAutoSaveWorkspace(): Promise<void> {
    try {
      await this.crmWorkspacesStore.loadWorkspaces();
      const workspaceId = this.crmWorkspacesStore.activeWorkspaceId();
      if (workspaceId !== null) await this.outlookWorkspace.persistSelection(workspaceId);
    } catch {
      this.toast.error('Unable to prepare the workspace for Outlook auto-save. Reopen the add-in to try again.');
    } finally {
      this.workspaceChanging.set(false);
    }
  }
}
